import React, { useEffect, useState } from "react";
import ConfirmModal from "../../Components/Modals/ConfirmModal";
import Notification from "../../Components/Core/Notification";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faComments,
  faFilePdf,
  faImage,
  faMicrophone,
} from "@fortawesome/free-solid-svg-icons";
import { useTenantContent } from "../../Context/TenantContentContext";
import {
  HasConsultation,
  RequestConsultationPayment,
} from "../../Services/ConsultionService";
import { GetUploadPermissions } from "../../Services/SettingService";
import { isLoggedIn } from "../../Services/AuthService";
import Loader from "../../Components/Core/Loader";
import SupportWidget from "../../Components/Core/SupportWidget";
import SeoHead from "../../Components/Core/SeoHead";
import { doctorDisplayName, joinUrl, resolveMediaUrl } from "../../utils/seo";
import { redirectToPaymentGateway } from "../../Services/paymentRedirect";
import GatewaySelector from "../../Components/Payment/GatewaySelector";

const ConsultationIndex = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [consultationFee, setConsultationFee] = useState(null);
  const [selectedGateway, setSelectedGateway] = useState("zarinpal");
  const [uploadPermissions, setUploadPermissions] = useState({
    allowPatientImageUpload: false,
    allowPatientPdfUpload: false,
    allowPatientVoiceUpload: false,
  });
  const navigate = useNavigate();
  const { content } = useTenantContent();

  const paymentGatewayMethod = Number(content?.paymentGatewayMethod ?? 1);
  const isPublicGateway = paymentGatewayMethod === 1;
  const canShowGatewaySelector = isPublicGateway;

  const handleRequestConsultation = async () => {
    try {
      if (!isLoggedIn()) {
        navigate("/login", { state: { from: "/counseling/index" } });
        return;
      }

      setLoading(true);

      const gateway = canShowGatewaySelector ? selectedGateway : "zarinpal";
      localStorage.setItem("consultationGateway", gateway);

      const requestResponse = await RequestConsultationPayment(gateway);

      if (
        requestResponse.code === 100 &&
        (requestResponse.authority || requestResponse.token)
      ) {
        redirectToPaymentGateway(requestResponse);
      } else {
        console.log("Payment request failed!");
      }
    } catch (error) {
      setLoading(false);
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      console.error(error);
    } finally {
      setOpenDialog(false);
    }
  };

  useEffect(() => {
    const redirectIfHasConsultation = async () => {
      try {
        setLoading(true);
        const [hasConsultation, permissions] = await Promise.all([
          HasConsultation(),
          GetUploadPermissions().catch(() => null),
        ]);

        setConsultationFee(hasConsultation.consultationFee);
        if (permissions) {
          setUploadPermissions({
            allowPatientImageUpload: Boolean(
              permissions.allowPatientImageUpload,
            ),
            allowPatientPdfUpload: Boolean(permissions.allowPatientPdfUpload),
            allowPatientVoiceUpload: Boolean(
              permissions.allowPatientVoiceUpload,
            ),
          });
        }
        if (hasConsultation.hasConsultation) {
          navigate("/counseling/chat/index");
        }
      } catch (error) {
        setSnackbarMessage(error.message);
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      } finally {
        setLoading(false);
      }
    };

    redirectIfHasConsultation();
  }, [navigate]);

  const feeInThousands =
    typeof consultationFee === "number"
      ? (consultationFee / 1000).toLocaleString()
      : "--";

  const allowImage = uploadPermissions.allowPatientImageUpload;
  const allowPdf = uploadPermissions.allowPatientPdfUpload;
  const allowVoice = uploadPermissions.allowPatientVoiceUpload;
  const extraCapabilities = [
    allowImage && "تصویر",
    allowPdf && "فایل",
    allowVoice && "پیام صوتی",
  ].filter(Boolean);

  let extraDescription = "";
  if (extraCapabilities.length === 1) {
    extraDescription = ` می‌توانید ${extraCapabilities[0]} هم ارسال کنید.`;
  } else if (extraCapabilities.length === 2) {
    extraDescription = ` می‌توانید ${extraCapabilities[0]} و ${extraCapabilities[1]} هم ارسال کنید.`;
  } else if (extraCapabilities.length === 3) {
    extraDescription = " می‌توانید تصویر، فایل و پیام صوتی هم ارسال کنید.";
  }

  if (!content) {
    return <Loader />;
  }

  const pageTitle =
    content.counselingPageTitle || `مشاوره آنلاین | ${content.siteName}`;
  const pageDescription =
    content.counselingPageDescription ||
    `شروع مشاوره آنلاین با ${doctorDisplayName(content)}، ${content.doctorTitle || content.doctorSpecialty || ""}. امکان ارسال سوالات و تصاویر برای دریافت راهنمایی تخصصی.`;
  const pageKeywords =
    content.counselingPageKeywords ||
    `مشاوره آنلاین, ${doctorDisplayName(content)}, ${content.doctorSpecialty}, ارسال سوال به پزشک`;
  const doctorPhoto = content.doctorAvatarUrl || content.doctorImageUrl;
  const ogImage =
    content.counselingOgImage || content.ogImage || content.doctorImageUrl;
  const pageUrl = joinUrl(content.canonicalUrl, "counseling/index");

  const features = [
    { key: "chat", label: "گفتگوی متنی", icon: faComments, show: true },
    { key: "image", label: "ارسال تصویر", icon: faImage, show: allowImage },
    { key: "file", label: "ارسال فایل", icon: faFilePdf, show: allowPdf },
    { key: "voice", label: "ارسال صوت", icon: faMicrophone, show: allowVoice },
  ].filter((item) => item.show);

  return (
    <>
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonical={pageUrl}
        image={ogImage}
        imageAlt={doctorDisplayName(content)}
        siteName={content.siteName}
      />

      {loading ? (
        <Loader />
      ) : (
        <div className="counseling-page iran-sans">
          <div className="counseling-card">
            <div className="counseling-hero">
              {doctorPhoto && (
                <img
                  src={resolveMediaUrl(doctorPhoto)}
                  alt={`دکتر ${content.doctorName}`}
                  className="counseling-hero-image"
                />
              )}
              <div className="counseling-hero-overlay">
                <span className="counseling-badge">بدون نوبت حضوری</span>
                <h1>مشاوره آنلاین</h1>
                <p>
                  دکتر {content.doctorName}
                  {content.doctorSpecialty
                    ? `، ${content.doctorSpecialty}`
                    : ""}
                </p>
              </div>
            </div>

            <div className="counseling-body">
              <p className="counseling-text">
                سؤالات خود را به‌راحتی بپرسید و از راهنمایی تخصصی بهره‌مند شوید.
                {extraDescription}
              </p>

              <div className="counseling-features">
                {features.map((feature) => (
                  <div className="counseling-feature" key={feature.key}>
                    <span className="counseling-feature-icon">
                      <FontAwesomeIcon icon={feature.icon} />
                    </span>
                    <span>{feature.label}</span>
                  </div>
                ))}
              </div>

              <div className="counseling-footer">
                <div className="counseling-fee">
                  <span>هزینه مشاوره</span>
                  <strong>{feeInThousands} هزار تومان</strong>
                </div>
                <button
                  type="button"
                  className="counseling-cta"
                  onClick={() => setOpenDialog(true)}
                >
                  درخواست مشاوره
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>
              </div>
            </div>
          </div>

          <ConfirmModal
            openDialog={openDialog}
            handleCloseDialog={() => setOpenDialog(false)}
            handleConfirm={handleRequestConsultation}
            text={[
              <>
                آیا مطمئن هستید که می‌خواهید مشاوره آنلاین با دکتر{" "}
                {content.doctorName} را شروع کنید؟ هزینه مشاوره مبلغ{" "}
                <strong>{feeInThousands} هزار تومان</strong> است. در صورت تایید،
                به صفحه پرداخت هدایت خواهید شد.{" "}
              </>,
              ...(content?.consultationPaymentModalContent ?? []),
            ]}
            cancleText="خیر"
            confirmText="تایید و پرداخت"
            title="درخواست مشاوره"
          >
            {canShowGatewaySelector && (
              <div style={{ marginTop: "12px" }}>
                <GatewaySelector
                  selectedGateway={selectedGateway}
                  onChange={setSelectedGateway}
                />
              </div>
            )}
          </ConfirmModal>
          <Notification
            openSnackbar={openSnackbar}
            snackbarMessage={snackbarMessage}
            snackbarSeverity={snackbarSeverity}
            setOpenSnackbar={setOpenSnackbar}
          />
        </div>
      )}
      <SupportWidget />

      <style jsx>{`
        .counseling-page {
          max-width: 640px;
          margin: 0.75rem auto 0;
          padding: 0 0.9rem 1rem;
          min-height: calc(100dvh - 56px);
          display: flex;
        }

        .counseling-card {
          display: flex;
          flex-direction: column;
          width: 100%;
          background: #fff;
          border: 1px solid rgba(5, 50, 81, 0.08);
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 12px 28px rgba(5, 50, 81, 0.08);
        }

        .counseling-hero {
          position: relative;
          height: 34vh;
          min-height: 210px;
          max-height: 280px;
          background: linear-gradient(135deg, #053251 0%, #075a86 100%);
        }

        .counseling-hero-image {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: top center;
          display: block;
        }

        .counseling-hero-overlay {
          position: absolute;
          inset: auto 0 0 0;
          padding: 3.2rem 1.2rem 1.1rem;
          background: linear-gradient(
            180deg,
            rgba(5, 50, 81, 0) 0%,
            rgba(5, 50, 81, 0.88) 100%
          );
          color: #fff;
          text-align: right;
        }

        .counseling-badge {
          display: inline-flex;
          background: rgba(124, 200, 144, 0.2);
          color: #c8f0d2;
          border: 1px solid rgba(124, 200, 144, 0.4);
          border-radius: 999px;
          padding: 0.2rem 0.75rem;
          font-size: 0.75rem;
          font-weight: 700;
        }

        .counseling-hero-overlay h1 {
          margin: 0.4rem 0 0.2rem;
          font-size: 1.7rem;
          font-weight: 800;
        }

        .counseling-hero-overlay p {
          margin: 0;
          color: rgba(255, 255, 255, 0.88);
          font-size: 0.9rem;
          line-height: 1.6;
        }

        .counseling-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding: 1.35rem 1.2rem 1.3rem;
        }

        .counseling-text {
          margin: 0;
          color: #4a6070;
          font-size: 0.98rem;
          line-height: 1.9;
          text-align: justify;
        }

        .counseling-features {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
          margin: auto 0;
          padding: 1.2rem 0;
        }

        .counseling-feature {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.55rem;
          min-height: 92px;
          background: #f4f8f6;
          border: 1px solid rgba(5, 50, 81, 0.06);
          border-radius: 16px;
          padding: 0.9rem 0.6rem;
          color: #053251;
          font-size: 0.88rem;
          font-weight: 700;
          text-align: center;
        }

        .counseling-feature-icon {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          background: rgba(124, 200, 144, 0.22);
          color: #3a8b4f;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.05rem;
        }

        .counseling-footer {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          margin-top: auto;
        }

        .counseling-fee {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          color: #6b7c88;
          font-size: 0.88rem;
        }

        .counseling-fee strong {
          color: #053251;
          font-size: 1.05rem;
        }

        .counseling-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          width: 100%;
          padding: 0.95rem 1.2rem;
          border: none;
          border-radius: 14px;
          background: #053251;
          color: #fff;
          font-family: inherit;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
        }

        .counseling-cta:hover {
          background: #04223f;
        }

        @media (max-width: 991.98px) {
          .counseling-page {
            box-sizing: border-box;
            min-height: 0;
            display: block;
            margin: 0.4rem auto 0;
            padding: 0 0.7rem calc(108px + env(safe-area-inset-bottom, 0px));
          }

          .counseling-hero {
            height: auto;
            min-height: 0;
            max-height: none;
            display: flex;
            flex-direction: row-reverse;
            align-items: center;
            gap: 0.85rem;
            padding: 0.85rem 1rem;
          }

          .counseling-hero-image {
            position: relative;
            inset: auto;
            width: 76px;
            height: 92px;
            border-radius: 16px;
            border: 2px solid rgba(255, 255, 255, 0.35);
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
            flex-shrink: 0;
            object-fit: cover;
            object-position: top center;
          }

          .counseling-hero-overlay {
            position: relative;
            inset: auto;
            padding: 0;
            background: none;
            flex: 1;
            min-width: 0;
          }

          .counseling-hero-overlay h1 {
            font-size: 1.28rem;
            margin: 0.28rem 0 0.12rem;
          }

          .counseling-hero-overlay p {
            font-size: 0.8rem;
            line-height: 1.45;
          }

          .counseling-badge {
            font-size: 0.68rem;
            padding: 0.12rem 0.6rem;
          }

          .counseling-body {
            flex: none;
            padding: 0.85rem 1rem 1rem;
          }

          .counseling-text {
            font-size: 0.84rem;
            line-height: 1.65;
          }

          .counseling-features {
            grid-auto-flow: column;
            grid-auto-columns: 1fr;
            grid-template-columns: none;
            gap: 0.5rem;
            margin: 0.7rem 0 0.85rem;
            padding: 0;
          }

          .counseling-feature {
            min-height: 0;
            gap: 0.35rem;
            padding: 0.55rem 0.3rem;
            font-size: 0.75rem;
          }

          .counseling-feature-icon {
            width: 34px;
            height: 34px;
            border-radius: 10px;
            font-size: 0.9rem;
          }

          .counseling-footer {
            gap: 0.65rem;
            margin-top: 0;
          }

          .counseling-cta {
            padding: 0.8rem 1rem;
          }
        }

        @media (max-width: 575.98px) {
          .counseling-page {
            margin: 0.3rem auto 0;
            padding: 0 0.55rem calc(108px + env(safe-area-inset-bottom, 0px));
          }

          .counseling-hero {
            gap: 0.7rem;
            padding: 0.7rem 0.85rem;
          }

          .counseling-hero-image {
            width: 64px;
            height: 80px;
            border-radius: 14px;
          }

          .counseling-hero-overlay h1 {
            font-size: 1.15rem;
          }

          .counseling-hero-overlay p {
            font-size: 0.75rem;
          }

          .counseling-body {
            padding: 0.7rem 0.85rem 0.85rem;
          }

          .counseling-text {
            font-size: 0.8rem;
            line-height: 1.55;
          }
        }

        @media (min-width: 768px) and (max-width: 991.98px) {
          .counseling-page {
            max-width: 720px;
            margin: 1rem auto 0;
          }

          .counseling-hero-image {
            width: 88px;
            height: 108px;
          }
        }

        @media (min-width: 992px) {
          .counseling-page {
            max-width: 1000px;
            min-height: calc(100dvh - 56px);
            margin: 0 auto;
            padding: 2.2rem 1.5rem 2.5rem;
            align-items: center;
          }

          .counseling-card {
            flex-direction: row;
            min-height: 540px;
            max-height: 620px;
          }

          .counseling-hero {
            width: 44%;
            height: auto;
            min-height: 100%;
            max-height: none;
            flex-shrink: 0;
          }

          .counseling-hero-overlay {
            padding: 4rem 1.6rem 1.5rem;
          }

          .counseling-hero-overlay h1 {
            font-size: 2rem;
          }

          .counseling-hero-overlay p {
            font-size: 1rem;
          }

          .counseling-body {
            width: 56%;
            padding: 2.4rem 2.2rem;
            justify-content: center;
          }

          .counseling-text {
            font-size: 1.05rem;
            text-align: right;
          }

          .counseling-features {
            margin: 1.6rem 0 1.8rem;
            padding: 0;
            gap: 0.9rem;
          }

          .counseling-feature {
            min-height: 110px;
            font-size: 0.95rem;
          }

          .counseling-footer {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          }

          .counseling-fee {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.2rem;
          }

          .counseling-cta {
            width: auto;
            min-width: 230px;
            padding: 0.95rem 1.6rem;
          }
        }
      `}</style>
    </>
  );
};

export default ConsultationIndex;
